/**
 * WebSocket Bill Processing History Page
 *
 * Dedicated history page for WS-based bill processing (Workflow 22).
 * Shows both pending tasks (needing approval) and completed workflows.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faClock,
  faSearch,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faArrowRight,
  faArrowLeft,
  faCoins,
  faFileInvoiceDollar,
  faWifi,
  faTasks,
  faEye,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { formatDistanceToNow } from 'date-fns';
import Cookies from 'js-cookie';
import MainLayout from '../Layout/MainLayout';
import { useAuth } from '../../context/AuthContext';

const WSBillHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'completed', 'all'
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agentId');
  const { user } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('sankalvax_token');

      // Load both sessions and tasks in parallel
      const [sessionsResponse, tasksResponse] = await Promise.all([
        fetch('/api/chat/sessions?limit=100', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch('/api/tasks?limit=100', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      const sessionsData = await sessionsResponse.json();
      const tasksData = await tasksResponse.json();

      // Filter sessions for workflow 22 (WS Bill Processing)
      const wsSessions = (sessionsData.sessions || []).filter(s =>
        s.workflow_id === 22 || s.workflow_id === '22'
      );

      // Filter tasks for workflow 22 (from task_data JSON)
      const wsTasks = (tasksData.tasks || []).filter(t => {
        try {
          const taskData = typeof t.task_data === 'string' ? JSON.parse(t.task_data) : t.task_data;
          return taskData?.workflow_id === 22 || taskData?.workflow_id === '22';
        } catch {
          return false;
        }
      });

      setSessions(wsSessions);
      setTasks(wsTasks);

      console.log('📋 WS History: Loaded', wsSessions.length, 'sessions,', wsTasks.length, 'WS tasks');
    } catch (error) {
      console.error('Failed to load WS history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (agentId) {
      navigate(`/agents/${agentId}`);
    } else {
      navigate('/agents');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: faCheckCircle, label: 'Completed' },
      draft: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', icon: faClock, label: 'Pending Review' },
      pending: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', icon: faClock, label: 'Pending' },
      failed: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', icon: faExclamationTriangle, label: 'Failed' },
      awaiting_user: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: faEye, label: 'Awaiting Review' }
    };
    return badges[status] || badges.pending;
  };

  // Helper to parse task_data (could be string or object)
  const parseTaskData = (taskData) => {
    if (!taskData) return null;
    try {
      return typeof taskData === 'string' ? JSON.parse(taskData) : taskData;
    } catch {
      return null;
    }
  };

  // Get combined list based on active tab
  const getFilteredItems = () => {
    let items = [];

    if (activeTab === 'pending') {
      // Show pending tasks
      items = tasks.filter(t => t.status === 'pending').map(t => {
        const taskData = parseTaskData(t.task_data);
        return {
          type: 'task',
          id: t.id,
          title: t.title,
          status: t.status,
          created_at: t.created_at,
          run_id: taskData?.run_id,
          session_id: taskData?.session_id,
          workflow_id: taskData?.workflow_id,
          workflow_name: taskData?.workflow_name,
          bill_amount: taskData?.bill_data?.total_amount,
          bill_currency: taskData?.bill_data?.currency,
          vendor_name: taskData?.bill_data?.vendor_name,
          task_data: taskData
        };
      });
    } else if (activeTab === 'completed') {
      // Show completed sessions
      items = sessions.filter(s => s.session_status === 'completed').map(s => ({
        type: 'session',
        id: s.session_id,
        title: s.session_name,
        status: s.session_status,
        created_at: s.created_at,
        run_id: s.run_id,
        session_id: s.session_id,
        bill_amount: s.bill_total_amount,
        bill_currency: s.bill_currency,
        vendor_name: s.session_name?.split('|')[0]?.trim()
      }));
    } else {
      // Show all
      const taskItems = tasks.map(t => {
        const taskData = parseTaskData(t.task_data);
        return {
          type: 'task',
          id: `task_${t.id}`,
          title: t.title,
          status: t.status,
          created_at: t.created_at,
          run_id: taskData?.run_id,
          session_id: taskData?.session_id,
          workflow_id: taskData?.workflow_id,
          workflow_name: taskData?.workflow_name,
          bill_amount: taskData?.bill_data?.total_amount,
          bill_currency: taskData?.bill_data?.currency,
          vendor_name: taskData?.bill_data?.vendor_name,
          task_data: taskData
        };
      });

      const sessionItems = sessions.map(s => ({
        type: 'session',
        id: `session_${s.session_id}`,
        title: s.session_name,
        status: s.session_status,
        created_at: s.created_at,
        run_id: s.run_id,
        session_id: s.session_id,
        bill_amount: s.bill_total_amount,
        bill_currency: s.bill_currency,
        vendor_name: s.session_name?.split('|')[0]?.trim()
      }));

      items = [...taskItems, ...sessionItems];
    }

    // Filter by search term
    if (searchTerm) {
      items = items.filter(item =>
        (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.vendor_name && item.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Sort by created_at (newest first)
    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const handleItemClick = (item) => {
    console.log('🔗 WSBillHistory: handleItemClick called with item:', item);
    console.log('🔗 WSBillHistory: run_id:', item.run_id, 'session_id:', item.session_id);

    if (item.run_id) {
      // Navigate to WS chat page with run_id
      const url = `/bill-ws-chat/${item.run_id}?name=${encodeURIComponent(item.vendor_name || 'Bill Processing')}`;
      console.log('🔗 WSBillHistory: Navigating to:', url);
      navigate(url);
    } else if (item.session_id) {
      // Fallback to session_id
      const url = `/bill-ws-chat/${item.session_id}`;
      console.log('🔗 WSBillHistory: Navigating to (fallback):', url);
      navigate(url);
    } else {
      console.error('🔗 WSBillHistory: No run_id or session_id found!', item);
    }
  };

  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const completedCount = sessions.filter(s => s.session_status === 'completed').length;

  if (loading) {
    return (
      <MainLayout title="WS Bill History">
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-purple-500 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading bill history...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const filteredItems = getFilteredItems();

  return (
    <MainLayout title="WS Bill History">
      <div className="max-w-6xl mx-auto p-6 space-y-6">

        {/* Back Button */}
        <div className="flex items-center mb-4">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-600 dark:text-gray-400"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span className="text-sm">Back to Agent</span>
          </button>
        </div>

        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-600 rounded-3xl mb-6 shadow-xl">
            <FontAwesomeIcon icon={faWifi} className="text-white text-2xl" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent mb-4">
            WebSocket Bill Processing
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-xl max-w-2xl mx-auto">
            Real-time bill processing history and pending approvals
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'pending'
                  ? 'bg-white dark:bg-gray-700 text-yellow-600 dark:text-yellow-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={faTasks} className="text-sm" />
              <span>Pending Review</span>
              <span className="ml-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400 text-xs rounded-full">
                {pendingCount}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'completed'
                  ? 'bg-white dark:bg-gray-700 text-green-600 dark:text-green-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="text-sm" />
              <span>Completed</span>
              <span className="ml-1 px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-xs rounded-full">
                {completedCount}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'all'
                  ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={faHistory} className="text-sm" />
              <span>All</span>
            </button>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-80">
            <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
            />
          </div>
        </div>

        {/* Items List */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-16">
            <FontAwesomeIcon
              icon={activeTab === 'pending' ? faTasks : faHistory}
              className="text-6xl text-gray-300 dark:text-gray-600 mb-6"
            />
            <h3 className="text-2xl font-semibold text-gray-500 dark:text-gray-400 mb-3">
              {searchTerm ? 'No matches found' : `No ${activeTab} bills`}
            </h3>
            <p className="text-gray-400 dark:text-gray-500">
              {searchTerm
                ? 'Try adjusting your search terms'
                : activeTab === 'pending'
                  ? 'No bills waiting for review'
                  : 'Start processing bills to see them here'
              }
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredItems.map((item) => {
              const badge = getStatusBadge(item.status);
              return (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:border-purple-200 dark:hover:border-purple-600 transition-all duration-300 cursor-pointer overflow-hidden group"
                >
                  <div className="p-8">
                    <div className="flex items-start justify-between">
                      {/* Left: Main Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start space-x-6">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg ${
                          item.status === 'completed'
                            ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                            : item.status === 'pending' || item.status === 'draft'
                            ? 'bg-gradient-to-br from-yellow-500 to-orange-500'
                            : 'bg-gradient-to-br from-purple-500 to-indigo-600'
                        }`}>
                          <FontAwesomeIcon
                            icon={faFileInvoiceDollar}
                            className="text-white text-lg"
                          />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                              {item.vendor_name || item.title || 'Unknown Vendor'}
                            </h3>

                            {/* Bill Amount Badge */}
                            {item.bill_amount && (
                              <div className="flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700">
                                <FontAwesomeIcon icon={faCoins} className="text-xs" />
                                <span>
                                  {item.bill_currency || '$'} {parseFloat(item.bill_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            )}

                            {/* Status Badge */}
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                              <FontAwesomeIcon icon={badge.icon} className="mr-1" />
                              {badge.label}
                            </span>
                          </div>

                          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                            <span className="flex items-center">
                              <FontAwesomeIcon icon={faClock} className="mr-1" />
                              {formatDate(item.created_at)}
                            </span>
                            {item.type === 'task' && (
                              <span className="flex items-center text-purple-500">
                                <FontAwesomeIcon icon={faWifi} className="mr-1" />
                                WebSocket
                              </span>
                            )}
                          </div>
                        </div>
                        </div>
                      </div>

                      {/* Right: Action Button */}
                      <div className="flex-shrink-0 ml-6">
                        <button
                          className={`flex items-center space-x-3 px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 ${
                            item.status === 'pending' || item.status === 'draft'
                              ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600'
                              : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700'
                          }`}
                        >
                          <FontAwesomeIcon icon={item.status === 'pending' || item.status === 'draft' ? faEye : faArrowRight} className="text-sm" />
                          <span>{item.status === 'pending' || item.status === 'draft' ? 'Review' : 'View'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer Stats */}
        <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                {pendingCount}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Pending Review</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                {completedCount}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {sessions.length}
              </div>
              <div className="text-gray-600 dark:text-gray-400">Total Sessions</div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default WSBillHistory;
